import { SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  Flex,
  Input,
  List,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  theme,
  Typography,
} from 'antd';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import ProductImage from '../productImage';
import useProductFilterOptions from '../../hooks/useProductFilterOptions';
import useStore from '../../store/useStore';
import { formatCurrency, getYmd } from '../../utils/format';
// Зөвхөн хуучин дэлгэцүүдийн (orders/Step1, orderReturn/.../ModalSelectOrder,
// orderReturn/.../ModalSelectProduct) классуудыг bundle-д үлдээхийн тулд импортлов.
// Энэ компонент өөрөө className хэрэглэхгүй.
import './style.less';

const { Search } = Input;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 56;

/** Зураг хавтгай харагдахгүйн тулд өгөх зөөлөн сүүдэр */
const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Анхны ачаалалтад харуулах хийсвэр мөрийн тоо */
const SKELETON_ROWS = 4;

/** Барааны нэрийг олж авах — эх сурвалж бүр өөр талбар нэртэй */
const getTitle = (product) => product?.title ?? product?.productTitle ?? '-';

/** Баркод байвал түүнийг, эс бөгөөс SKU / багц кодыг харуулна */
const getCode = (product) =>
  product?.barCodes?.[0] ?? product?.productSku ?? product?.batchCode ?? '-';

/** Үнэ — бараа/агуулахын эх сурвалжаас хамаарч price эсвэл unitPrice */
const getPrice = (product) => {
  const price = parseFloat(product?.price ?? product?.unitPrice);
  return Number.isFinite(price) ? formatCurrency(price) : '-';
};

/** Үлдэгдэл — агуулахын боломжит үлдэгдэл эсвэл барааны нөөц */
const getStock = (product) => product?.availableStock ?? product?.stock;

/** Нэг барааны мөр — дарахад сонгогдоно, hover/сонгогдсон төлөв нь дэвсгэрээр */
const ProductRow = ({ product, showStock, onToggle, token }) => {
  const [hovered, setHovered] = useState(false);
  const checked = Boolean(product?.checked);
  const stock = getStock(product);
  const title = getTitle(product);

  const background = checked
    ? token.colorPrimaryBg
    : hovered
      ? token.colorFillTertiary
      : undefined;

  return (
    <List.Item
      onClick={() => onToggle(!checked)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 12px',
        marginBottom: 4,
        borderRadius: 8,
        cursor: 'pointer',
        background,
        // Сонгогдсоныг дэвсгэрээс гадна нимгэн хүрээгээр тодотгоно
        boxShadow: checked ? `inset 0 0 0 1px ${token.colorPrimaryBorder}` : undefined,
        transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
      }}
    >
      <Flex align="center" gap={12} style={{ width: '100%' }}>
        <ProductImage
          src={product?.imageCover || product?.productImage}
          alt={title}
          size={IMAGE_SIZE}
          style={{
            objectFit: 'contain',
            borderRadius: 8,
            border: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            boxShadow: IMAGE_SHADOW,
          }}
        />

        <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
          <Text strong ellipsis={{ tooltip: title }}>
            {title}
          </Text>
          <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
            {getCode(product)}
          </Text>
        </Flex>

        <Flex
          vertical
          align="flex-end"
          gap={2}
          style={{ minWidth: 140, fontSize: 12, whiteSpace: 'nowrap' }}
        >
          <Text strong>{getPrice(product)}</Text>
          {showStock && stock != null && <Text type="secondary">Үлдэгдэл: {stock}ш</Text>}
          {product?.willExpire && (
            <Text type="secondary">Дуусах: {getYmd(product?.expiryDate)}</Text>
          )}
        </Flex>

        {/* Мөр дээр дарахтай давхцаж, сонголтыг буцааж болзошгүй тул зогсооно */}
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        </span>
      </Flex>
    </List.Item>
  );
};

const ModalSelectProductView = ({
  isModalVisible,
  handleOk,
  handleCancel,
  onSearchProduct,
  productList = [],
  searchText,
  searchResult,
  handleCheckProduct,
  showFilter = false,
  filterValues = {},
  onFilterChange = () => undefined,
  loading = false,
  // Дэлгэцийн голд байрлуулах эсэх — хуучин дэлгэцүүд дээрх байрлал өөрчлөгдөхгүй
  centered = false,
}) => {
  const contractType = useStore((state) => state.user.contractType);
  const { token } = theme.useToken();

  //Modal нээгдсэн үед л Брэнд, Категорийн сонголтуудыг татна
  const { brandList, categoryList, brandLoading, categoryLoading } = useProductFilterOptions(
    showFilter && isModalVisible
  );

  // Сонголтын төлвийг жагсаалтаас нь шууд тооцно (тусдаа state хадгалахгүй)
  const checkedCount = productList.filter((product) => product?.checked).length;
  const allChecked = productList.length > 0 && checkedCount === productList.length;

  const onCheckAll = (e) => {
    productList.forEach((_, index) => handleCheckProduct(index, e.target.checked));
  };

  const brandOptions = brandList.map((brand, index) => ({
    key: brand?._id ?? index,
    value: brand?.name,
    label: brand?.name,
  }));

  const categoryOptions = categoryList.map((category, index) => ({
    key: category?._id ?? index,
    value: category?.code,
    label: category?.name,
  }));

  return (
    <Modal
      title="Бараа сонгох"
      open={isModalVisible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={640}
      centered={centered}
      footer={
        <Flex align="center" justify="space-between" gap={16}>
          {/* Сонголт хийгээгүй үед зай нь хэвээр үлдэж товчнууд үсрэхгүй */}
          <Text type="secondary">
            {checkedCount > 0 ? `${checkedCount} бараа сонгогдсон` : ''}
          </Text>
          <Space>
            <Button onClick={handleCancel}>Буцах</Button>
            <Button type="primary" onClick={handleOk} disabled={checkedCount === 0}>
              Бараа сонгох
            </Button>
          </Space>
        </Flex>
      }
    >
      {/* Хайлт, шүүлтүүр, "Бүгдийг сонгох" — жагсаалтын дээр тогтмол байрлана */}
      <Flex vertical gap={12} style={{ paddingTop: 16 }}>
        <Search
          placeholder="Барааны нэр, код, баркодоор хайх"
          allowClear
          enterButton={<SearchOutlined />}
          loading={loading}
          // antd-ийн Search нь `onSearch(value, event, info)` гэж 3 дахь
          // аргумент дамжуулдаг. Харин `onSearchProduct`-ийн 3 дахь параметр нь
          // `page` тул тэр объект хуудасны дугаарын оронд орж, хүсэлт рүү
          // `page=[object Object]` явж, хуудаслалт замхардаг байв.
          onSearch={(value) => onSearchProduct(value)}
          // Хайлтаа цэвэрлэхэд бүх бараа буцаж ирнэ
          onChange={(e) => {
            if (e.target.value === '') onSearchProduct('');
          }}
        />

        {showFilter && (
          <Row gutter={[8, 12]}>
            <Col xs={24} sm={12}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                Брэнд
              </Text>
              <Select
                placeholder="Бүгд"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={filterValues?.brand || undefined}
                onChange={(value) => onFilterChange('brand', value ?? '')}
                options={brandOptions}
                loading={brandLoading}
              />
            </Col>
            <Col xs={24} sm={12}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
                Категори
              </Text>
              <Select
                placeholder="Бүгд"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                value={filterValues?.category || undefined}
                onChange={(value) => onFilterChange('category', value ?? '')}
                options={categoryOptions}
                loading={categoryLoading}
              />
            </Col>
          </Row>
        )}

        <Divider style={{ margin: 0 }} />

        <Flex align="center" justify="space-between" gap={16}>
          <Checkbox
            checked={allChecked}
            indeterminate={checkedCount > 0 && !allChecked}
            disabled={productList.length === 0}
            onChange={onCheckAll}
          >
            Бүгдийг сонгох
          </Checkbox>
          {productList.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {checkedCount} / {productList.length} сонгогдсон
            </Text>
          )}
        </Flex>
      </Flex>

      <div id="scrollableDiv" style={{ width: '100%', overflow: 'auto' }}>
        {loading && productList.length === 0 ? (
          /* Ачаалж байхад "олдсонгүй" гэж андуурч харуулахгүйн тулд хийсвэр мөрүүд */
          <div style={{ padding: '16px 12px 0' }}>
            {Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <Skeleton
                key={index}
                active
                avatar={{ shape: 'square', size: IMAGE_SIZE }}
                title={false}
                paragraph={{ rows: 2, width: ['60%', '35%'] }}
                style={{ marginBottom: 20 }}
              />
            ))}
          </div>
        ) : productList.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Бараа олдсонгүй."
            style={{ padding: '48px 0' }}
          />
        ) : (
          /* Мөрүүд аль хэдийн байвал (хайлт/шүүлтүүр солих) хуучныг нь бүдгэрүүлж spinner */
          <Spin spinning={loading}>
            <InfiniteScroll
              dataLength={productList.length}
              height={440}
              pullDownToRefreshThreshold={5}
              next={() => onSearchProduct(searchText, null, searchResult?.page + 1)}
              hasMore={searchResult?.hasNextPage}
              loader={<Skeleton avatar paragraph={{ rows: 1 }} />}
              scrollableTarget="scrollableDiv"
            >
              <List
                // Мөр бүр өөрийн radius/дэвсгэртэй тул хуваах зураас илүүц
                split={false}
                dataSource={productList}
                renderItem={(product, index) => (
                  <ProductRow
                    key={product?._id ?? index}
                    product={product}
                    showStock={contractType !== 'O'}
                    onToggle={(checked) => handleCheckProduct(index, checked)}
                    token={token}
                  />
                )}
              />
            </InfiniteScroll>
          </Spin>
        )}
      </div>
    </Modal>
  );
};

export default ModalSelectProductView;
